import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, CheckCircle2, Link2Off } from 'lucide-react';
import { toast } from 'sonner';
import whatsappService from '../services/whatsappService';

interface WhatsAppConnectionCardProps {
  /** Called after a successful unlink, so the parent can refresh its own state. */
  onDisconnected?: () => void;
}

/**
 * Shows the currently connected WhatsApp (WABA) number with an "unlink" action.
 * Renders nothing while loading or when no number is connected. Fetches its own
 * status so it can be dropped anywhere without prop wiring.
 */
export const WhatsAppConnectionCard: React.FC<WhatsAppConnectionCardProps> = ({ onDisconnected }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [number, setNumber] = useState<string | undefined>(undefined);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const loadStatus = async () => {
    setIsLoading(true);
    try {
      const status = await whatsappService.getStatus();
      setIsConnected(status.status === 'connected');
      setNumber(status.whatsapp_number);
    } catch (error) {
      // Unknown status: keep the card hidden rather than claiming a state we
      // could not confirm — but make the failure visible in the console.
      console.error('Error fetching WhatsApp status:', error);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleUnlink = async () => {
    const confirmed = window.confirm(
      'WhatsApp-Verknüpfung wirklich aufheben? Die Nummer wird bei WhatsApp/Meta abgemeldet. ' +
        'Du kannst danach jederzeit erneut verknüpfen.',
    );
    if (!confirmed) return;

    setIsUnlinking(true);
    try {
      const res = await whatsappService.disconnect();
      if (res.deregistered) {
        toast.success('WhatsApp-Verknüpfung aufgehoben und Nummer bei Meta abgemeldet.');
      } else {
        toast.success(
          'WhatsApp-Verknüpfung aufgehoben. Die Meta-Abmeldung konnte nicht bestätigt werden ' +
            '(die Nummer war evtl. schon abgemeldet) — neu verknüpfen ist trotzdem möglich.',
        );
      }
      setIsConnected(false);
      setNumber(undefined);
      onDisconnected?.();
    } catch (error: any) {
      toast.error(error?.message || 'Aufheben der Verknüpfung fehlgeschlagen.');
    } finally {
      setIsUnlinking(false);
    }
  };

  if (isLoading || !isConnected) return null;

  return (
    <Card className="border-border">
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success-strong" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">WhatsApp verbunden</p>
            <p className="truncate font-mono text-sm text-muted-foreground">{number || '—'}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleUnlink}
          disabled={isUnlinking}
          className="border-destructive/30 text-destructive-strong hover:bg-destructive/15"
        >
          {isUnlinking ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Link2Off className="mr-2 h-4 w-4" />
          )}
          Verknüpfung aufheben
        </Button>
      </CardContent>
    </Card>
  );
};

export default WhatsAppConnectionCard;
